// client/src/components/chat/MessageComposer.js
import { useState, useRef, useEffect } from 'react'
import { CHAT_CONSTANTS } from '../../utils/chatUtils'

function MessageComposer({
  onSendMessage,
  disabled,
  onStartTyping,
  onStopTyping,
  autoFocus = false,
}) {
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [isTyping, setIsTyping] = useState(false)
  const textareaRef = useRef(null)
  const typingTimeoutRef = useRef(null)

  // Auto-focus the textarea when autoFocus prop changes to true
  useEffect(() => {
    if (!autoFocus || disabled) {
      return
    }

    if (!textareaRef.current) {
      return
    }

    // Use a small timeout for reliable focus after render and async channel changes
    const timeoutId = setTimeout(() => {
      textareaRef.current?.focus()
    }, 100)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [autoFocus, disabled])

  const handleSubmit = async (e) => {
    e.preventDefault()

    const trimmedMessage = message.trim()
    if (!trimmedMessage || sending || disabled) {
      return
    }

    setSending(true)

    try {
      // Stop typing when sending message
      if (isTyping && onStopTyping) {
        setIsTyping(false)
        onStopTyping()
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }

      await onSendMessage(trimmedMessage)
      setMessage('')

      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
    } catch (error) {
      console.error('Failed to send message:', error)
    } finally {
      setSending(false)
    }
  }

  const handleKeyDown = (e) => {
    // Standard Enter sends message
    if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) {
      e.preventDefault()
      handleSubmit(e)
    }
    // Ctrl+Enter also sends message (common chat pattern)
    else if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  const handleTextareaChange = (e) => {
    setMessage(e.target.value)

    // Auto-resize textarea
    const textarea = e.target
    textarea.style.height = 'auto'
    textarea.style.height = `${Math.min(
      textarea.scrollHeight,
      CHAT_CONSTANTS.TEXTAREA_MAX_HEIGHT,
    )}px`

    // Handle typing indicators
    if (e.target.value.trim().length > 0) {
      // Start typing if not already typing
      if (!isTyping && onStartTyping) {
        setIsTyping(true)
        onStartTyping()
      }

      // Reset typing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }

      // Stop typing after 2 seconds of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        if (isTyping && onStopTyping) {
          setIsTyping(false)
          onStopTyping()
        }
      }, 2000)
    } else {
      // Stop typing if input is empty
      if (isTyping && onStopTyping) {
        setIsTyping(false)
        onStopTyping()
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }
    }
  }

  const canSend = message.trim().length > 0 && !sending && !disabled

  return (
    <div className="message-composer">
      <form
        className="composer-form"
        onSubmit={handleSubmit}
        aria-label="Send message form"
      >
        <label htmlFor="message-input" className="sr-only">
          Type your message
        </label>
        <textarea
          id="message-input"
          ref={textareaRef}
          className="composer-input"
          placeholder={
            disabled ? 'Select a channel to send messages' : 'Type a message...'
          }
          value={message}
          onChange={handleTextareaChange}
          onKeyDown={handleKeyDown}
          disabled={sending || disabled}
          rows="1"
          maxLength={CHAT_CONSTANTS.MESSAGE_MAX_LENGTH}

          aria-describedby="message-help-text"
        />
        <span id="message-help-text" className="sr-only">
          Press Enter to send, Shift+Enter for new line, Ctrl+Enter to send
        </span>
        <button
          type="submit"
          className="send-button"
          disabled={!canSend}
          aria-label={
            sending
              ? 'Sending message...'
              : canSend
                ? 'Send message'
                : 'Cannot send empty message'
          }
        >
          {sending ? (
            <div
              className="loading-spinner"
              style={{
                width: '16px',
                height: '16px',
                borderTopColor: '#007bff',
              }}
              role="status"
              aria-label="Sending"
            />
          ) : (
            'Send'
          )}
        </button>
      </form>
    </div>
  )
}

export default MessageComposer
